import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import PlanCard from "@/components/PlanCard";
import Layout from "@/components/Layout";
import HomeAnalytics from "@/components/HomeAnalytics";
import HeroCarousel from "@/components/HeroCarousel";
import InteractiveExperiences from "@/components/InteractiveExperiences";
import TrustedWorldwide from "@/components/TrustedWorldwide";
import { Button } from "@/components/ui/button";
import {
  ArrowRight,
  CheckCircle2,
  Building2,
  Boxes,
  Palette,
  Award,
  Globe,
  Clock,
  Quote,
  ChevronLeft,
  ChevronRight,
  ArrowUpRight,
} from "lucide-react";
import { usePublicSettings } from "@/lib/publicSettings";

/**
 * Iter52 — Homepage Premium Refinement (Item #7)
 *
 * Rewritten for a boutique international architecture studio feel:
 *   • Cinematic hero with slow zoom + staggered fade-up copy
 *   • Editorial rule pattern (short accent line + overline label)
 *   • Statistics band with hairline dividers
 *   • Bento-style category grid (1 large + 6 medium)
 *   • Refined "Why Choose" cards with hairline circle icons
 *   • Editorial custom-design split with luxe pricing panel
 *   • Full-bleed forest CTA with cream copy
 *
 * All copy, imagery and section visibility is CMS-driven — do NOT hardcode.
 */

// Iter52 — Premium hero fallback (luxury modern architectural exterior).
// Admin can override via /admin/homepage → Hero → background image or via
// /admin/branding → Hero Banner.
const HERO_IMG =
  "https://images.unsplash.com/photo-1613490493576-7fde63acd811?crop=entropy&cs=srgb&fm=jpg&w=2400&q=85&ixlib=rb-4.1.0";

const MIN_ROUND_UP = 20; // "Round-up" only kicks in once we ship 20+ plans.
function fmtLibrarySize(total) {
  if (!Number.isFinite(total) || total <= 0) return null;
  if (total < MIN_ROUND_UP) return String(total);
  const rounded = Math.floor(total / 10) * 10;
  return `${rounded}+`;
}

// Bento category grid — the first tile spans 2×2 on large screens for an
// editorial magazine feel.
const CATEGORIES = [
  { key: "Modern", label: "Modern House Plans", img: "https://images.pexels.com/photos/323780/pexels-photo-323780.jpeg?auto=compress&cs=tinysrgb&w=1600", tag: "Editorial minimalism" },
  { key: "Luxury Villa", label: "Luxury Villas", img: "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=1200&q=80", tag: "5,000+ sqft estates" },
  { key: "Farmhouse", label: "Farmhouse Plans", img: "https://images.pexels.com/photos/7031581/pexels-photo-7031581.jpeg?auto=compress&cs=tinysrgb&w=1200", tag: "Warm & timber-framed" },
  { key: "Duplex", label: "Duplex Plans", img: "https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?auto=format&fit=crop&w=1200&q=80", tag: "Multi-generational" },
  { key: "Traditional", label: "Traditional Homes", img: "https://images.unsplash.com/photo-1580587771525-78b9dba3b914?auto=format&fit=crop&w=1200&q=80", tag: "Classical proportions" },
  { key: "Small", label: "Small House Plans", img: "https://images.unsplash.com/photo-1518005020951-eccb494ad742?auto=format&fit=crop&w=1200&q=80", tag: "Under 1,500 sqft" },
  { key: "Contemporary", label: "Contemporary Designs", img: "https://images.pexels.com/photos/7031622/pexels-photo-7031622.jpeg?auto=compress&cs=tinysrgb&w=1200", tag: "Bold & sculpted" },
];

const WHY = [
  { icon: Award, label: "Established Since 2012", note: "13+ years of practice" },
  { icon: Building2, label: "Curated Plan Library", note: "Every plan build-ready" },
  { icon: Palette, label: "Custom Design Available", note: "Personalize any plan" },
  { icon: Boxes, label: "BIM & Revit Experts", note: "Full LOD 400 modeling" },
  { icon: Globe, label: "International Experience", note: "Delivered to 40+ countries" },
  { icon: Clock, label: "Fast Support", note: "24-hour response guarantee" },
];

const TESTIMONIALS = [
  {
    quote:
      "We browsed for two months before finding Dream Home Engineers. The plan we chose was exactly the modern floor plan we'd been sketching in our heads — and the team helped us adapt it for our sloped lot at a fraction of the price of a custom architect.",
    name: "Homeowners",
    role: "Austin, Texas · USA",
  },
  {
    quote:
      "As a custom builder I've bought stock plans from four different marketplaces. The drawing set from Dream Home Engineers is the cleanest I've received — dimensioned properly, permit-ready, and their team answered every callout question within a day.",
    name: "Custom Home Builder",
    role: "Toronto · Canada",
  },
  {
    quote:
      "The BIM model was so detailed our contractor said it eliminated at least 40 hours of RFI back-and-forth. Dream Home Engineers delivered on time and to spec — worth every rupee.",
    name: "Homeowners",
    role: "Sydney · Australia",
  },
];

// Small "eyebrow" component — short accent rule + uppercase micro-label.
const Eyebrow = ({ children, className = "", ...rest }) => (
  <div className={`flex items-center gap-4 ${className}`} {...rest}>
    <span className="hairline-accent w-10 shrink-0" />
    <span className="overline text-accent">{children}</span>
  </div>
);

const Home = () => {
  const { settings } = usePublicSettings();
  const hero = settings?.homepage_hero || {};
  const heroSlidesCms = settings?.homepage_hero_slides || {};
  const experiencesCms = settings?.homepage_experiences || {};
  const trustedCms = settings?.homepage_trusted || {};
  const marketing = settings?.homepage_marketing || {};
  const seo = settings?.homepage_seo || {};
  const customCms = settings?.homepage_custom_design || {};
  const testimonialsCms = settings?.homepage_testimonials || {};
  const stats = settings?.homepage_stats?.stats || [];
  const sectionsFlags = settings?.homepage_sections || {};
  const isOn = (key) => sectionsFlags?.[key]?.enabled !== false;

  const [featured, setFeatured] = useState([]);
  const [counts, setCounts] = useState({ total: 0, by_style: {} });
  const [testimonialIdx, setTestimonialIdx] = useState(0);

  // Live list of testimonials: CMS-driven with fallback to compiled defaults
  // so the section never renders empty during initial settings fetch.
  const testimonialItems = (testimonialsCms.items && testimonialsCms.items.length > 0)
    ? testimonialsCms.items
    : TESTIMONIALS;

  useEffect(() => {
    api.get("/plans", { params: { featured: true, limit: 8 } })
      .then((r) => setFeatured(r.data))
      .catch(() => setFeatured([]));
    api.get("/plan-counts")
      .then((r) => setCounts(r.data))
      .catch(() => {});
  }, []);

  // Iter53 — Wire the SEO CMS block into <head> so admin edits actually
  // affect what search engines see. Runs whenever `seo` changes.
  useEffect(() => {
    const prevTitle = document.title;
    if (seo.page_title) document.title = seo.page_title;

    const upsertMeta = (name, content) => {
      if (content == null || content === "") return () => {};
      let el = document.querySelector(`meta[name="${name}"]`);
      const created = !el;
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("name", name);
        document.head.appendChild(el);
      }
      const prev = el.getAttribute("content");
      el.setAttribute("content", content);
      return () => {
        if (created) el.remove();
        else if (prev != null) el.setAttribute("content", prev);
      };
    };

    const upsertCanonical = (href) => {
      if (!href) return () => {};
      let el = document.querySelector('link[rel="canonical"]');
      const created = !el;
      if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", "canonical");
        document.head.appendChild(el);
      }
      const prev = el.getAttribute("href");
      el.setAttribute("href", href);
      return () => {
        if (created) el.remove();
        else if (prev != null) el.setAttribute("href", prev);
      };
    };

    const cleanupDesc = upsertMeta("description", seo.meta_description);
    const cleanupKw = upsertMeta(
      "keywords",
      Array.isArray(seo.meta_keywords) ? seo.meta_keywords.join(", ") : (seo.meta_keywords || "")
    );
    const cleanupCanon = upsertCanonical(seo.canonical_url);

    return () => {
      document.title = prevTitle;
      cleanupDesc();
      cleanupKw();
      cleanupCanon();
    };
  }, [seo.page_title, seo.meta_description, seo.canonical_url, JSON.stringify(seo.meta_keywords || [])]);

  const testimonial = testimonialItems[testimonialIdx % testimonialItems.length] || testimonialItems[0];
  const librarySize = fmtLibrarySize(counts.total);
  const librarySizePhrase = librarySize ? `${librarySize} house plans` : "a curated collection of house plans";
  const librarySizeShort = librarySize || "the collection";

  const heroBg =
    hero.background_image_url ||
    settings?.company?.hero_banner_url ||
    HERO_IMG;

  return (
    <Layout>
      {/* ══════════════════════════════ HERO ══════════════════════════════ */}
      {isOn("hero") && (() => {
        // Iter54b — if the CMS has any enabled hero slides, render the new
        // multi-slide carousel; otherwise fall back to the legacy single hero
        // so existing sites keep working during the transition.
        const enabledSlides = (heroSlidesCms.slides || []).filter(
          (s) => s && s.enabled !== false && (s.media_url || s.media_provider_url || s.poster_url)
        );
        if (enabledSlides.length > 0) {
          return (
            <HeroCarousel
              slides={heroSlidesCms.slides}
              carousel={heroSlidesCms.carousel}
              librarySize={librarySize}
            />
          );
        }
        return (
          <section data-testid="hero-section" className="relative isolate">
          <div className="relative w-full h-[86vh] min-h-[620px] max-h-[900px] overflow-hidden bg-primary">
            <img
              src={heroBg}
              alt="Boutique architectural home exterior — Dream Home Engineers"
              className="absolute inset-0 w-full h-full object-cover anim-slow-zoom"
              loading="eager"
              data-testid="hero-banner-img"
            />

            {/* Cinematic gradient — deep bottom, editorial top-fade */}
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                background:
                  `linear-gradient(180deg, rgba(0,0,0,0.35) 0%, rgba(0,0,0,${(typeof hero.overlay_opacity === "number" ? hero.overlay_opacity : 0.55)}) 45%, rgba(0,0,0,0.82) 100%)`,
              }}
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/50 via-transparent to-transparent pointer-events-none hidden md:block" />

            {/* Copy block — bottom-left */}
            <div className="absolute inset-0 flex items-end">
              <div className="max-w-[1400px] mx-auto w-full px-6 lg:px-12 pb-16 lg:pb-24">
                <div className="max-w-3xl text-primary-foreground">
                  <div className="anim-fade-up anim-delay-1" data-testid="hero-eyebrow">
                    <Eyebrow className="text-accent">
                      {hero.eyebrow || `Since 2012 · ${librarySizePhrase} · 40+ countries`}
                    </Eyebrow>
                  </div>

                  <h1 className="font-display font-light leading-[0.92] tracking-tighter mt-7 text-5xl sm:text-6xl lg:text-7xl anim-fade-up anim-delay-2">
                    {hero.heading_line1 || "Beautifully designed,"}
                    <br />
                    <span className="italic text-accent">
                      {hero.heading_line2_italic || "professionally engineered"}
                    </span>{" "}
                    {hero.heading_line3 || "house plans."}
                  </h1>

                  <p className="mt-7 text-base sm:text-lg lg:text-xl text-primary-foreground/85 max-w-xl leading-relaxed anim-fade-up anim-delay-3">
                    {hero.description ||
                      `Trusted by homeowners, builders, and developers worldwide. Browse ${librarySizePhrase} — modern, luxury, farmhouse, duplex, traditional and contemporary designs ready to build or customize.`}
                  </p>

                  <div className="mt-10 flex flex-wrap gap-4 anim-fade-up anim-delay-4">
                    <Link to={hero.primary_cta_href || "/plans"}>
                      <Button
                        data-testid="hero-browse-btn"
                        size="lg"
                        className="lux-btn rounded-none bg-accent hover:bg-accent/95 text-accent-foreground h-14 px-9 text-base tracking-wide"
                      >
                        {hero.primary_cta_label || "Browse House Plans"}{" "}
                        <ArrowRight size={18} className="ml-2" />
                      </Button>
                    </Link>
                    <Link to={hero.secondary_cta_href || "/custom-design"}>
                      <Button
                        data-testid="hero-custom-btn"
                        size="lg"
                        variant="outline"
                        className="lux-btn rounded-none h-14 px-9 border-primary-foreground/50 text-primary-foreground bg-transparent hover:bg-primary-foreground hover:text-primary text-base tracking-wide"
                      >
                        {hero.secondary_cta_label || "Request Custom Design"}
                      </Button>
                    </Link>
                  </div>

                  {/* Small credential row */}
                  <div className="mt-12 hidden md:flex items-center gap-6 text-primary-foreground/70 anim-fade-up anim-delay-5">
                    <div className="overline">Trusted worldwide</div>
                    <div className="hairline w-24 opacity-40" />
                    <div className="flex items-center gap-5 text-xs tracking-[0.2em] uppercase">
                      <span>USA</span>
                      <span className="opacity-40">·</span>
                      <span>Canada</span>
                      <span className="opacity-40">·</span>
                      <span>UK</span>
                      <span className="opacity-40">·</span>
                      <span>Australia</span>
                      <span className="opacity-40">·</span>
                      <span>India</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Floating "Featured" chip — top-right on lg */}
            <div
              className="absolute top-8 right-8 lg:top-12 lg:right-12 hidden md:block anim-fade-up anim-delay-3"
              data-testid="hero-featured-chip"
            >
              <div className="bg-background/95 backdrop-blur border border-border p-5 max-w-[260px] shadow-2xl">
                <Eyebrow>{hero.featured_chip_title || "Featured this month"}</Eyebrow>
                <div className="font-display text-xl mt-3 leading-snug text-foreground">
                  {hero.featured_chip_subtitle ||
                    "The Solstice Villa — 5,800 sqft of unapologetic luxury."}
                </div>
                <Link
                  to="/plans?featured=true"
                  data-testid="hero-featured-view-plan"
                  className="mt-4 inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.22em] text-accent hover:text-accent/80 transition-fast"
                >
                  View plan <ArrowUpRight size={12} />
                </Link>
              </div>
            </div>

            {/* Scroll indicator */}
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 hidden lg:flex flex-col items-center gap-2 text-primary-foreground/60 anim-fade-in anim-delay-6">
              <div className="text-[10px] uppercase tracking-[0.3em]">Scroll</div>
              <div className="w-px h-10 bg-primary-foreground/40" />
            </div>
          </div>
        </section>
        );
      })()}

      {/* ═════════════════════════ TRUSTED WORLDWIDE ═════════════════════════ */}
      {isOn("trusted") && (
        <TrustedWorldwide
          section={trustedCms.section}
          countries={trustedCms.countries}
        />
      )}

      {/* ═════════════════════════ STATISTICS BAND ═════════════════════════ */}
      {isOn("statistics") && (
        <section className="bg-primary text-primary-foreground border-b border-primary-foreground/10">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
            <div className="grid grid-cols-2 lg:grid-cols-4 divide-x divide-primary-foreground/10">
              {stats
                .filter((r) => r.enabled !== false)
                .map((row, idx) => {
                  const isAuto = String(row.value || "").toLowerCase() === "auto";
                  const display = isAuto
                    ? librarySize
                      ? librarySize.replace("+", "")
                      : null
                    : row.value;
                  const showSuffix = isAuto
                    ? librarySize && librarySize.endsWith("+")
                    : !!row.suffix;
                  const slug = (row.id || row.label || "row")
                    .toString()
                    .toLowerCase()
                    .replace(/[^a-z0-9]+/g, "-")
                    .replace(/^-|-$/g, "") || "row";
                  return (
                    <div
                      key={row.id || row.label}
                      data-testid={`stat-${slug}`}
                      className={`py-16 lg:py-20 px-6 lg:px-8 ${idx === 0 ? "pl-0 lg:pl-2" : ""}`}
                    >
                      <div
                        className={`font-display text-6xl lg:text-7xl leading-none ${
                          row.italic ? "italic" : ""
                        }`}
                      >
                        {display == null ? (
                          <span className="italic text-accent">Curated</span>
                        ) : (
                          <>
                            {row.italic ? (
                              <span className="text-accent">{display}</span>
                            ) : (
                              display
                            )}
                            {showSuffix && (
                              <span className="text-accent">
                                {isAuto ? "+" : row.suffix}
                              </span>
                            )}
                          </>
                        )}
                      </div>
                      <div className="overline text-primary-foreground/70 mt-5">
                        {row.label}
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        </section>
      )}

      {/* ══════════════════════════ BROWSE BY STYLE ══════════════════════════ */}
      {isOn("browse_by_style") && (
        <section
          data-testid="categories-section"
          className="max-w-[1400px] mx-auto px-6 lg:px-12 py-24 lg:py-32"
        >
          <div className="flex flex-wrap items-end justify-between gap-6 mb-16">
            <div className="max-w-xl">
              <Eyebrow>Featured Categories</Eyebrow>
              <h2 className="font-display text-5xl lg:text-6xl mt-5 tracking-tighter leading-[0.95]">
                Browse by <span className="italic text-accent">style</span>
              </h2>
              <p className="mt-5 text-muted-foreground text-base lg:text-lg leading-relaxed">
                Seven signature aesthetics — each shaped by our in-house architects and refined
                across thousands of real builds worldwide.
              </p>
            </div>
            <Link
              to="/plan-collections"
              data-testid="see-all-collections"
              className="overline text-accent hover:text-accent/70 transition-fast inline-flex items-center gap-2"
            >
              All collections <ArrowUpRight size={14} />
            </Link>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 lg:auto-rows-[240px] gap-5 lg:gap-8">
            {CATEGORIES.map((cat, i) => {
              const count = counts.by_style?.[cat.key] ?? 0;
              // Bento layout — first tile spans 2×2 on lg for an editorial feel.
              const isFeature = i === 0;
              const spanClass = isFeature
                ? "lg:col-span-2 lg:row-span-2 aspect-[4/5] lg:aspect-auto"
                : "aspect-[4/5] lg:aspect-auto";
              return (
                <Link
                  key={cat.key}
                  to={`/plans?style=${encodeURIComponent(cat.key)}`}
                  data-testid={`category-${cat.key}`}
                  className={`group relative overflow-hidden bg-muted lift-hover ${spanClass}`}
                >
                  <img
                    src={cat.img}
                    alt={`${cat.label} — architectural collection`}
                    className="plan-card-img w-full h-full object-cover"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-primary/95 via-primary/30 to-transparent" />
                  <div className="absolute inset-0 flex flex-col justify-end p-6 lg:p-8 text-primary-foreground">
                    <div className="overline text-accent">{cat.tag}</div>
                    <div
                      className={`font-display leading-[0.95] mt-3 tracking-tight ${
                        isFeature ? "text-4xl lg:text-5xl" : "text-2xl lg:text-3xl"
                      }`}
                    >
                      {cat.label}
                    </div>
                    <div className="mt-4 flex items-center justify-between text-xs">
                      <span className="tracking-widest uppercase opacity-80">
                        {count} {count === 1 ? "plan" : "plans"}
                      </span>
                      <span className="inline-flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-fast text-accent">
                        Explore <ArrowUpRight size={13} />
                      </span>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </section>
      )}

      {/* ══════════════════════════ FEATURED PLANS ══════════════════════════ */}
      {isOn("featured_plans") && (
        <section className="bg-muted/30 border-y border-border">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-24 lg:py-32">
            <div className="flex flex-wrap items-end justify-between gap-6 mb-16">
              <div className="max-w-xl">
                <Eyebrow>Editor's picks</Eyebrow>
                <h2 className="font-display text-5xl lg:text-6xl mt-5 tracking-tighter leading-[0.95]">
                  Featured <span className="italic text-accent">house plans</span>
                </h2>
                <p className="mt-5 text-muted-foreground text-base lg:text-lg leading-relaxed">
                  Handpicked this month — the plans most requested by homeowners and builders
                  across the US, Canada, UK, and Australia.
                </p>
              </div>
              <Link
                to="/plans"
                data-testid="see-all-plans"
                className="overline text-accent hover:text-accent/70 transition-fast inline-flex items-center gap-2"
              >
                All {librarySizeShort} <ArrowUpRight size={14} />
              </Link>
            </div>
            <div
              data-testid="featured-plans-grid"
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-10"
            >
              {featured.map((plan) => (
                <PlanCard key={plan.id} plan={plan} />
              ))}
              {featured.length === 0 && (
                <div className="col-span-full text-muted-foreground text-center py-16">
                  Loading featured plans…
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* ══════════════════════════ WHY CHOOSE US ══════════════════════════ */}
      {isOn("why_choose") && (
        <section className="max-w-[1400px] mx-auto px-6 lg:px-12 py-24 lg:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-20">
            <div className="lg:col-span-5">
              <Eyebrow>Why Choose Us</Eyebrow>
              <h2 className="font-display text-5xl lg:text-6xl mt-5 leading-[0.95] tracking-tighter">
                Why homeowners and builders choose{" "}
                <span className="italic text-accent">Dream Home Engineers.</span>
              </h2>
              <p className="mt-8 text-muted-foreground text-lg leading-relaxed max-w-md">
                We're not a stock-plan reseller. Every drawing in our library was created in-house
                by our 25-person team of architects, drafters, and 3D artists — and improved with
                the feedback of thousands of real builds.
              </p>
              <Link to="/about">
                <Button
                  className="lux-btn lux-btn-primary rounded-none bg-primary hover:bg-primary/90 px-9 mt-12 h-12"
                  data-testid="why-cta"
                >
                  Learn more about us <ArrowRight size={16} className="ml-2" />
                </Button>
              </Link>
            </div>
            <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {WHY.map(({ icon: Icon, label, note }) => (
                <div
                  key={label}
                  className="border border-border/70 p-7 flex gap-5 items-start bg-background/60 lift-hover"
                  data-testid={`why-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                >
                  <div className="w-12 h-12 shrink-0 rounded-full border border-accent/30 flex items-center justify-center text-accent">
                    <Icon size={20} strokeWidth={1.5} />
                  </div>
                  <div>
                    <div className="font-display text-xl leading-tight">{label}</div>
                    <div className="text-xs text-muted-foreground mt-1.5 tracking-wide">{note}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ══════════════════════════ INTERACTIVE EXPERIENCES ══════════════════════════ */}
      {isOn("experiences") && (
        <InteractiveExperiences
          section={experiencesCms.section}
          items={experiencesCms.items}
        />
      )}

      {/* ══════════════════════════ CUSTOM DESIGN STUDIO ══════════════════════════ */}
      {isOn("custom_design") && (
        <section className="relative bg-primary text-primary-foreground overflow-hidden" data-testid="custom-design-section">
          <img
            src="https://images.pexels.com/photos/4458210/pexels-photo-4458210.jpeg?auto=compress&cs=tinysrgb&w=1800"
            alt=""
            aria-hidden
            className="absolute inset-0 w-full h-full object-cover opacity-15"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary via-primary/90 to-primary/60" />
          <div className="relative max-w-[1400px] mx-auto px-6 lg:px-12 py-24 lg:py-32 grid grid-cols-1 lg:grid-cols-12 gap-14 items-center">
            <div className="lg:col-span-7">
              <Eyebrow>{customCms.eyebrow || "Custom Design Studio"}</Eyebrow>
              <h2 className="font-display text-5xl lg:text-7xl mt-5 leading-[0.9] tracking-tighter">
                {customCms.heading_line1 || "Your lot. Your vision."}
                <br />
                <span className="italic text-accent">{customCms.heading_italic || "Our architects."}</span>
              </h2>
              <p className="mt-7 text-lg text-primary-foreground/80 max-w-xl leading-relaxed">
                {customCms.description ||
                  "Found a plan you love but need to change something? Buy any of our house plans and our design team will modify it to fit your site, your family, and your local code — for a flat fee, with unlimited revisions until you're delighted."}
              </p>
              <div className="mt-10 space-y-3.5">
                {(customCms.bullets && customCms.bullets.length > 0
                  ? customCms.bullets
                  : [
                      "Adapt any plan to your lot dimensions",
                      "Change bedrooms, kitchen, garage layout",
                      "Convert to metric / imperial for local permits",
                      "3D renderings and virtual walkthroughs included",
                    ]
                ).map((line, i) => (
                  <div
                    key={`bullet-${i}`}
                    className="flex items-center gap-3.5 text-primary-foreground/90"
                    data-testid={`custom-bullet-${i}`}
                  >
                    <CheckCircle2 size={17} className="text-accent shrink-0" strokeWidth={1.75} />
                    <span>{line}</span>
                  </div>
                ))}
              </div>
              <div className="mt-12 flex flex-wrap gap-4">
                <Link to={customCms.primary_cta_href || "/custom-design?type=modify"}>
                  <Button
                    size="lg"
                    className="lux-btn rounded-none bg-accent hover:bg-accent/95 text-accent-foreground h-14 px-9 tracking-wide"
                    data-testid="custom-modify-btn"
                  >
                    {customCms.primary_cta_label || "Customize Existing Plan"}
                  </Button>
                </Link>
                <Link to={customCms.secondary_cta_href || "/custom-design?type=new"}>
                  <Button
                    size="lg"
                    variant="outline"
                    className="lux-btn rounded-none h-14 px-9 border-primary-foreground/50 text-primary-foreground bg-transparent hover:bg-primary-foreground hover:text-primary tracking-wide"
                    data-testid="custom-new-btn"
                  >
                    {customCms.secondary_cta_label || "Start New Design"}
                  </Button>
                </Link>
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="border border-primary-foreground/20 p-9 bg-primary/60 backdrop-blur-md" data-testid="custom-pricing-panel">
                <Eyebrow>{customCms.pricing_headline || marketing.custom_design_headline || "Studio pricing"}</Eyebrow>
                <div className="mt-6 divide-y divide-primary-foreground/10">
                  {(customCms.tiers && customCms.tiers.length > 0
                    ? customCms.tiers
                    : [
                        { label: "Minor modifications", subtitle: "Layout tweaks", price: "$150" },
                        { label: "Major redesign", subtitle: "Room-by-room rework", price: "$300" },
                        { label: "Full custom home", subtitle: "Below 3,000 sqft", price: "$500" },
                      ]
                  ).map((tier, i) => (
                    <div key={`tier-${i}`} className="flex justify-between items-baseline py-5" data-testid={`custom-tier-${i}`}>
                      <div>
                        <div className="font-display text-lg">{tier.label}</div>
                        {tier.subtitle && (
                          <div className="text-xs text-primary-foreground/50 mt-1 tracking-wider uppercase">
                            {tier.subtitle}
                          </div>
                        )}
                      </div>
                      <span className="font-display text-3xl">{tier.price}</span>
                    </div>
                  ))}
                </div>
                <p className="mt-7 text-xs text-primary-foreground/60 leading-relaxed">
                  {customCms.pricing_footer ||
                    marketing.custom_design_footer ||
                    "Fixed-fee quotes. Unlimited revisions. Delivered in 2-3 weeks depending on scope & requirement."}
                </p>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ══════════════════════════ TESTIMONIALS ══════════════════════════ */}
      {isOn("testimonials") && testimonialItems.length > 0 && (
        <section className="max-w-[1400px] mx-auto px-6 lg:px-12 py-24 lg:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-4">
              <Eyebrow>{testimonialsCms.eyebrow || "Client Stories"}</Eyebrow>
              <h2 className="font-display text-5xl lg:text-6xl mt-5 leading-[0.95] tracking-tighter">
                {testimonialsCms.heading_line1 || "Loved by homeowners on"}{" "}
                <span className="italic text-accent">
                  {testimonialsCms.heading_italic || "four continents."}
                </span>
              </h2>
              <div className="mt-10 flex gap-3">
                <button
                  onClick={() =>
                    setTestimonialIdx((i) => (i - 1 + testimonialItems.length) % testimonialItems.length)
                  }
                  data-testid="testimonial-prev"
                  aria-label="Previous testimonial"
                  className="w-12 h-12 border border-border hover:border-accent hover:text-accent transition-fast flex items-center justify-center"
                >
                  <ChevronLeft size={18} />
                </button>
                <button
                  onClick={() => setTestimonialIdx((i) => (i + 1) % testimonialItems.length)}
                  data-testid="testimonial-next"
                  aria-label="Next testimonial"
                  className="w-12 h-12 border border-border hover:border-accent hover:text-accent transition-fast flex items-center justify-center"
                >
                  <ChevronRight size={18} />
                </button>
              </div>
            </div>
            <div className="lg:col-span-8" data-testid="testimonial-card">
              <Quote size={56} className="text-accent" strokeWidth={1.25} />
              <blockquote className="mt-8 font-display text-2xl lg:text-4xl leading-[1.2] tracking-tight max-w-3xl">
                "{testimonial.quote}"
              </blockquote>
              <div className="mt-10 flex items-center gap-5">
                <div className="w-14 h-14 bg-primary text-primary-foreground font-display text-2xl flex items-center justify-center">
                  {(testimonial.name || "?").split(" ").map((n) => n[0]).slice(0, 2).join("")}
                </div>
                <div>
                  <div className="font-display text-lg">{testimonial.name}</div>
                  <div className="text-sm text-muted-foreground tracking-wider">
                    {testimonial.role}
                  </div>
                </div>
              </div>
              <div className="mt-8 flex gap-2">
                {testimonialItems.map((t, i) => (
                  <button
                    key={`t-${i}`}
                    onClick={() => setTestimonialIdx(i)}
                    data-testid={`testimonial-dot-${i}`}
                    className={`h-[2px] transition-fast ${
                      i === testimonialIdx ? "w-12 bg-accent" : "w-6 bg-border"
                    }`}
                    aria-label={`Go to testimonial ${i + 1}`}
                  />
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ══════════════════════════ FINAL CTA ══════════════════════════ */}
      {isOn("final_cta") && (
        <section className="bg-primary text-primary-foreground relative overflow-hidden" data-testid="final-cta-section">
          <div className="absolute inset-0 opacity-[0.08] noise-bg pointer-events-none" />
          <div className="relative max-w-[1400px] mx-auto px-6 lg:px-12 py-24 lg:py-32 text-center">
            <div className="inline-flex items-center gap-4 justify-center">
              <span className="hairline-accent w-12" />
              <span className="overline text-accent">{marketing.final_cta_eyebrow || "Start building"}</span>
              <span className="hairline-accent w-12" />
            </div>
            <h2 className="font-display text-5xl lg:text-7xl mt-8 leading-[0.95] tracking-tighter max-w-4xl mx-auto">
              {marketing.final_cta_heading_line1 || "Your dream home starts with the"}{" "}
              <span className="italic text-accent">
                {marketing.final_cta_heading_italic || "right plan."}
              </span>
            </h2>
            <p className="mt-8 text-primary-foreground/70 text-lg max-w-xl mx-auto leading-relaxed">
              {marketing.final_cta_description ||
                `Browse ${librarySize ? `${librarySizeShort} of ready-to-build plans` : "our curated collection"}, or commission a custom design shaped exactly to your lot and lifestyle.`}
            </p>
            <div className="mt-12 flex flex-wrap gap-4 justify-center">
              <Link to={marketing.final_cta_primary_href || "/plans"}>
                <Button
                  size="lg"
                  className="lux-btn rounded-none bg-accent hover:bg-accent/95 text-accent-foreground h-14 px-10 text-base tracking-wide"
                  data-testid="final-cta-primary-btn"
                >
                  {marketing.final_cta_primary_label ||
                    `Browse ${librarySize ? `all ${librarySizeShort}` : "the collection"}`}{" "}
                  <ArrowRight size={18} className="ml-2" />
                </Button>
              </Link>
              <Link to={marketing.final_cta_secondary_href || "/contact"}>
                <Button
                  size="lg"
                  variant="outline"
                  className="lux-btn rounded-none h-14 px-10 border-primary-foreground/50 text-primary-foreground bg-transparent hover:bg-primary-foreground hover:text-primary text-base tracking-wide"
                  data-testid="final-cta-secondary-btn"
                >
                  {marketing.final_cta_secondary_label || "Talk to an architect"}
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}
      <HomeAnalytics />
    </Layout>
  );
};

export default Home;
